import {
  IconChartDots2,
  IconHeadphones,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import BookMarkFillIcon from "../../assets/icons/bookmark-fill.svg?react";
import BookMarkSimpleIcon from "../../assets/icons/bookmark-simple.svg?react";
import ShareV2Icon from "../../assets/icons/share-v2.svg?react";
import { cn } from "../../lib/utils";
import type { PublicationCardRecommendation } from "./PublicationCard";
import { Tag, type TagVariant } from "./Tag";
import { SourceLabel, type PublicationSource } from "./SourceLabel";

// ── Types ───────────────────────────────────────────────────────────────

type ContentType = "video" | "infographic" | "podcast";

type CommonProps = {
  contentType: ContentType;
  title: string;
  source: PublicationSource | null;
  date: string;
  /** Content-type label, rendered as the chip that opens the meta row. */
  meta: string;
  metaVariant: TagVariant;
  detail: string | null;
  recommendation: PublicationCardRecommendation | null;
  isBookmarked: boolean;
  onBookmark: (() => void) | null;
  onShare: (() => void) | null;
  onClick: (() => void) | null;
  className?: string;
};

type SmProps = CommonProps & {
  size: "sm";
  thumbnailUrl: string | null;
};

type LgProps = CommonProps & {
  size?: "lg";
  imageUrl: string | null;
};

type ContentPublicationCardProps = SmProps | LgProps;

// ── Subparts ────────────────────────────────────────────────────────────

function useRecommendationLabel(
  reason: PublicationCardRecommendation["reason"]
): string {
  const { t } = useTranslation();
  switch (reason) {
    case "fromRoom":
      return t("From");
    case "follow":
      return t("Because you follow");
    case "specialty":
      return t("Based on your specialty");
    case "meshInterest":
      return t("Based on your interest in");
    case "embeddingSimilar":
      return t("Recommended for you");
    case "landmark":
      return t("Essential reading");
    case "exploration":
      return t("Discover something new");
    case "recommendedForYou":
      return t("Recommended for you");
    default:
      throw new Error(reason satisfies never);
  }
}

function RecommendationBadge({
  value,
}: {
  value: PublicationCardRecommendation;
}) {
  const label = useRecommendationLabel(value.reason);
  return (
    <div className="flex flex-col w-full min-w-0">
      <div className="flex gap-0.75 items-center text-[11px]/4 tracking-[-0.11px] w-full min-w-0">
        <span className="font-normal text-ds-slate-600 whitespace-nowrap shrink-0">
          {label}
        </span>
        {value.reason !== "recommendedForYou" &&
          value.reason !== "embeddingSimilar" &&
          value.reason !== "landmark" &&
          value.reason !== "exploration" && (
            <span className="font-medium text-ds-black truncate min-w-0">
              {value.topic}
            </span>
          )}
      </div>
      <div className="ml-0.75 h-3 w-px bg-ds-slate-300" />
    </div>
  );
}

function ContentIcon({
  contentType,
  size,
}: {
  contentType: ContentType;
  size: "sm" | "lg";
}) {
  const dimension = size === "sm" ? "size-5" : "size-6";
  const iconSize = size === "sm" ? 20 : 24;
  const Icon =
    contentType === "video"
      ? IconPlayerPlay
      : contentType === "infographic"
        ? IconChartDots2
        : IconHeadphones;
  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-md bg-ds-white border border-ds-black/6",
        dimension
      )}
    >
      <Icon size={iconSize} stroke={1.5} className="text-ds-black" />
    </div>
  );
}

function ActionRow({
  isBookmarked,
  onBookmark,
  onShare,
}: {
  isBookmarked: boolean;
  onBookmark: (() => void) | null;
  onShare: (() => void) | null;
}) {
  const { t } = useTranslation();
  if (onBookmark === null && onShare === null) return null;
  return (
    <div className="flex h-10 items-center justify-end gap-2 w-full">
      {onBookmark === null ? null : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onBookmark();
          }}
          aria-label={isBookmarked ? t("Saved") : t("Bookmark")}
          aria-pressed={isBookmarked}
          className="inline-flex items-center justify-center size-10 cursor-pointer text-ds-black"
        >
          {isBookmarked ? (
            <BookMarkFillIcon className="size-6" />
          ) : (
            <BookMarkSimpleIcon className="size-6" />
          )}
        </button>
      )}
      {onShare === null ? null : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShare();
          }}
          aria-label={t("Share")}
          className="inline-flex items-center justify-center size-10 cursor-pointer text-ds-black"
        >
          <ShareV2Icon className="size-6" />
        </button>
      )}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────

function ContentPublicationCard(props: ContentPublicationCardProps) {
  const {
    contentType,
    title,
    source,
    date,
    meta,
    metaVariant,
    detail,
    recommendation,
    isBookmarked,
    onBookmark,
    onShare,
    onClick,
    className,
  } = props;
  const size = props.size === "sm" ? "sm" : "lg";

  const journalLine =
    source === null ? null : (
      <div className="flex h-5 items-center w-full min-w-0">
        <span className="truncate text-footnote">
          <SourceLabel source={source} onNameClick={null} />
        </span>
      </div>
    );

  const titleText = (
    <p className="font-dm-sans font-semibold text-[15px]/5 tracking-[-0.15px] text-ds-black w-full m-0">
      {title}
    </p>
  );

  const metaRow = (
    // Wraps when the content-type chip is long: the duration and the date stay
    // together and drop to a new line instead of crushing the chip.
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-caption font-normal text-ds-slate-600">
      <Tag
        size="sm"
        variant={metaVariant}
        state="idle"
        icon={null}
        className="shrink-0"
      >
        {meta}
      </Tag>
      <div className="flex items-center gap-1 whitespace-nowrap">
        {detail === null ? null : <span>· {detail}</span>}
        <span>· {date}</span>
      </div>
    </div>
  );

  const rootCls = cn(
    "bg-white flex flex-col items-start py-3 w-full font-dm-sans",
    onClick === null ? null : "cursor-pointer text-left",
    className
  );

  const content = (
    <>
      {recommendation === null ? null : (
        <RecommendationBadge value={recommendation} />
      )}
      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-col gap-2 w-full">
          {size !== "sm" ? (
            <>
              {journalLine}
              <div
                className={cn(
                  "relative w-full rounded-xl overflow-hidden bg-ds-slate-100",
                  contentType === "video" ? "aspect-video" : "h-36"
                )}
              >
                {props.size !== "sm" && props.imageUrl !== null ? (
                  <img
                    src={props.imageUrl}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : null}
                <div className="absolute bottom-2.5 right-2.5">
                  <ContentIcon contentType={contentType} size="lg" />
                </div>
              </div>
            </>
          ) : (
            <>
              {journalLine}
              <div className="flex gap-3 items-center w-full">
                <div className="flex-1 min-w-0">{titleText}</div>
                <div className="relative size-13.5 rounded-md overflow-hidden shrink-0 bg-ds-slate-100">
                  {props.size === "sm" && props.thumbnailUrl !== null ? (
                    <img
                      src={props.thumbnailUrl}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : null}
                  <div className="absolute bottom-1 right-1">
                    <ContentIcon contentType={contentType} size="sm" />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        {size !== "sm" ? (
          <div className="flex flex-col gap-0.5 w-full">
            {titleText}
            <div className="flex items-center gap-2 w-full">
              <div className="flex-1 min-w-0">{metaRow}</div>
              <div className="shrink-0">
                <ActionRow
                  isBookmarked={isBookmarked}
                  onBookmark={onBookmark}
                  onShare={onShare}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 w-full">
            <div className="flex-1 min-w-0">{metaRow}</div>
            <div className="shrink-0">
              <ActionRow
                isBookmarked={isBookmarked}
                onBookmark={onBookmark}
                onShare={onShare}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (onClick === null) {
    return <div className={rootCls}>{content}</div>;
  }
  return (
    <button type="button" onClick={onClick} className={rootCls}>
      {content}
    </button>
  );
}

export { ContentPublicationCard, type ContentPublicationCardProps };
